import { buildMatchKey } from "../computeQuantityDiff";
import type { PlanV3 } from "../../../utils/types";
import type { DiffSide } from "../computeQuantityDiff";
import type { MarkerTypeBucket, RouteTypeBucket } from "./types";

// Group ids that normalize to one key are the same type spelled differently;
// merging them keeps a plan from matching the same counterpart twice.
export const buildMarkerBuckets = (
  plan: PlanV3,
  side: DiffSide,
): Map<string, MarkerTypeBucket> => {
  const buckets = new Map<string, MarkerTypeBucket>();
  for (const groupId of plan.groupedElements.groupIds) {
    const key = buildMatchKey(groupId, side);
    const markers = plan.groupedElements.byGroupId[groupId] ?? [];
    const existingBucket = buckets.get(key);
    if (existingBucket === undefined) {
      buckets.set(key, { displayName: groupId, markers: [...markers] });
      continue;
    }
    for (const marker of markers) {
      existingBucket.markers.push(marker);
    }
  }
  return buckets;
};

export const buildRouteBuckets = (
  plan: PlanV3,
  side: DiffSide,
): Map<string, RouteTypeBucket> => {
  const buckets = new Map<string, RouteTypeBucket>();
  for (const routeGroup of plan.routeGroups) {
    const key = buildMatchKey(routeGroup.label, side);
    const existingBucket = buckets.get(key);
    if (existingBucket === undefined) {
      buckets.set(key, {
        displayName: routeGroup.label,
        routes: [...routeGroup.routes],
      });
      continue;
    }
    for (const route of routeGroup.routes) {
      existingBucket.routes.push(route);
    }
  }
  return buckets;
};

// Before keys first, then after-only keys, so entry order never depends on Map internals.
export const unionKeysInOrder = <TBucket>(
  beforeBuckets: Map<string, TBucket>,
  afterBuckets: Map<string, TBucket>,
): string[] => {
  const keys = [...beforeBuckets.keys()];
  for (const key of afterBuckets.keys()) {
    if (beforeBuckets.has(key)) continue;
    keys.push(key);
  }
  return keys;
};
